import "./BusinessImpactOverview.scss";

import * as R from "ramda";

import { Channel1, Channel2, Channel3, Channel4 } from "../../utils/colors";
import {
  DATE_GROUPING_OPTIONS_BRAND_EQUITY,
  formatModelOverviewOutcomeVariable,
  groupChartDataByDate,
} from "../BrandEquityUtils";
import { DownloadDropdown, Dropdown, DropdownToggleType, InfoTooltip } from "../../Components";
import React, { useCallback, useMemo, useState } from "react";
import { capitalizeWords, currencyFormatter, numberFormatter } from "../../MMM/MMMUtils";
import { downloadPNG, exportToExcel } from "../../utils/download-utils";

import AreaChart from "../../Components/Charts/AreaChart";
import ChartContainer from "../../Components/ChartContainer";
import { DateGroupingKey } from "../../TVADCrossChannel/homePageConstants";
import DynamicModelOverview from "../../Components/DynamicModelOverview/DynamicModelOverview";
import { IncrementalRoasTakeaways } from "@blisspointmedia/bpm-types/dist/BrandEquity";
import LineChart from "../../Components/Charts/LineChart";
import WidgetContainer from "../../Components/WidgetContainer";
import { dateToMddyy } from "../../utils/date-utils";
import { formatNumber } from "../../utils/format-utils";
import { percFormat } from "../BrandImpact/BrandImpactUtils";
import { useCompanyInfo } from "../../redux/company";

interface ModelOverview {
  client: string;
  kpi: string;
  kpi_type: string;
  date_granularity: string;
  refresh_date: string;
  refresh_timestamp: string;
  train_start_date: string;
  train_end_date: string;
  test_start_date: string;
  test_end_date: string;
  train_size: number;
  test_size: number;
  train_rsquared: number;
  test_rsquared: number;
  test_mape: number;
  test_mae: number;
  n_observations: number;
  mean_spend: number;
  median_spend: number;
}

interface WeeklyRevenueTimeSeries {
  date: string;
  revenue: number;
}

interface BusinessImpactOverviewProps {
  company: string;
  groupByMetric: string;
  weeklyBrandHealthTimeSeries: any[];
  weeklyRevenueTimeSeries: WeeklyRevenueTimeSeries[];
  modelOverviewAll: ModelOverview;
  incrementalRoasTakeaways: IncrementalRoasTakeaways;
  isQv: boolean;
  queryVolumeLongLabel: string;
  queryVolumeShortLabel: string;
}

const colors = [Channel1, Channel3, Channel2, Channel4];

export const BusinessImpactOverview: React.FC<BusinessImpactOverviewProps> = ({
  company,
  groupByMetric,
  weeklyBrandHealthTimeSeries,
  weeklyRevenueTimeSeries,
  modelOverviewAll: modelOverview,
  incrementalRoasTakeaways,
  isQv,
  queryVolumeLongLabel,
  queryVolumeShortLabel,
}) => {
  const [weeklyRevenueChartDateGrouping, setWeeklyRevenueChartDateGrouping] = useState("Week");
  const [weeklyBrandHealthChartDateGrouping, setWeeklyBrandHealthChartDateGrouping] = useState(
    "Week"
  );
  const { fiscal_cal: fiscalCal } = useCompanyInfo();

  const { res: weeklyBrandHealthTimeSeriesByDate } = useMemo(() => {
    if (weeklyBrandHealthTimeSeries.length > 0) {
      const groupByDateGqvData = groupChartDataByDate(
        weeklyBrandHealthTimeSeries,
        weeklyBrandHealthChartDateGrouping as DateGroupingKey,
        fiscalCal
      );
      return { res: groupByDateGqvData };
    }
    return { res: [] };
  }, [weeklyBrandHealthChartDateGrouping, weeklyBrandHealthTimeSeries, fiscalCal]);

  const { res: finalWeeklyBrandHealthAreaChartData } = useMemo(() => {
    if (weeklyRevenueTimeSeries.length > 0) {
      const groupByDateGqvData = groupChartDataByDate(
        weeklyRevenueTimeSeries,
        weeklyRevenueChartDateGrouping as DateGroupingKey,
        fiscalCal
      );
      return { res: groupByDateGqvData };
    }
    return { res: [] };
  }, [weeklyRevenueChartDateGrouping, weeklyRevenueTimeSeries, fiscalCal]);

  const keysWithoutDate = useMemo(
    () =>
      Object.keys(weeklyBrandHealthTimeSeries[0])
        .filter(key => key !== "date")
        .sort(),
    [weeklyBrandHealthTimeSeries]
  );

  const excelDownloadWeeklyBrandHealth = useCallback(() => {
    exportToExcel(weeklyBrandHealthTimeSeriesByDate, "weekly_brand_health");
  }, [weeklyBrandHealthTimeSeriesByDate]);

  const excelDownloadWeeklySpend = useCallback(() => {
    exportToExcel(weeklyRevenueTimeSeries, "weekly_spend");
  }, [weeklyRevenueTimeSeries]);

  const excelDownloadModelOverview = useCallback(() => {
    exportToExcel([modelOverview], "model_overview");
  }, [modelOverview]);

  const pngDownloadWeeklyBrandHealth = useCallback(async () => {
    await downloadPNG(
      ".businessImpactOverviewleft > .chartContainer:nth-child(2",
      "weekly_brand_health"
    );
  }, []);

  const pngDownloadWeeklyRevenue = useCallback(async () => {
    await downloadPNG(".businessImpactOverviewleft .chartContainer", "weekly_revenue");
  }, []);

  const pngDownloadModelOverview = useCallback(async () => {
    await downloadPNG(".businessImpactOverview .brandEquityRight", "model_overview");
  }, []);

  return (
    <WidgetContainer
      collapsible
      header={"Overview"}
      subHeader={
        <>
          Assessing the impact of brand equity on Revenue outcomes.
          <InfoTooltip rightLabel="More info" size="sm">
            The goal is to understand and quantify the independent effect of paid media on a set of
            brand health outcomes. We employ a multivariate time series model that regresses each
            brand outcome on the set of paid media activities and a series of control variables. In
            the estimation, the model accounts for non-linearities in effects and the often
            significant timing differences between inputs and outputs.
          </InfoTooltip>
        </>
      }
    >
      {!R.isEmpty(modelOverview) &&
        !R.isEmpty(weeklyBrandHealthTimeSeries) &&
        !R.isEmpty(weeklyRevenueTimeSeries) && (
          <div className="businessImpactOverview">
            <div className="businessImpactOverviewleft">
              <ChartContainer
                enableHoverDesign
                rightActions={
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownloadWeeklySpend, pngDownloadWeeklyRevenue]}
                  />
                }
                title={
                  <>
                    <Dropdown
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklyRevenueChartDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklyRevenueChartDateGrouping(option)}
                    />
                    <div>Revenue</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  weeklyRevenueTimeSeries[0].date
                )} – ${dateToMddyy(
                  weeklyRevenueTimeSeries[weeklyRevenueTimeSeries.length - 1].date
                )}`}
              >
                <AreaChart
                  data={finalWeeklyBrandHealthAreaChartData}
                  xAxisDataKey="date"
                  xAxisTickFormatter={val => dateToMddyy(val)}
                  dateGrouping="Week"
                  yAxisWidth={75}
                  yAxisTickFormatter={val => `${currencyFormatter.format(val, 0)}`}
                  tooltipFormatter={val => {
                    if (!val) {
                      return val;
                    }
                    return val < 1000
                      ? currencyFormatter.format(val, 0)
                      : currencyFormatter.format(val, 1);
                  }}
                  tooltipShape="line"
                  areas={[
                    {
                      name: "Revenue",
                      dataKey: "revenue",
                    },
                  ]}
                  hardCodedColorName="Revenue"
                  hardCodedColor="#0194FF"
                ></AreaChart>
              </ChartContainer>
              <ChartContainer
                enableHoverDesign
                rightActions={
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownloadWeeklyBrandHealth, pngDownloadWeeklyBrandHealth]}
                  />
                }
                title={
                  <>
                    <Dropdown
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklyBrandHealthChartDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklyBrandHealthChartDateGrouping(option)}
                    />
                    <div>Brand Health</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  weeklyBrandHealthTimeSeries[0].date
                )} – ${dateToMddyy(
                  weeklyBrandHealthTimeSeries[weeklyBrandHealthTimeSeries.length - 1].date
                )}
                `}
                beforeTooltipText={isQv ? "Search Intent" : "Positive Response"}
                tooltipText={
                  isQv
                    ? `${queryVolumeLongLabel} (${queryVolumeShortLabel}) measures changes in the volume of branded search intent over time. We use ${queryVolumeShortLabel} to proxy for brand equity as it represents a funnel step between awareness and an actual site visit or install.`
                    : "Positive response indicates the percentage of survey respondents expressing favorable perceptions or experiences related to each brand health metric."
                }
              >
                <LineChart
                  customTooltipHeader={val => `Week of ${dateToMddyy(val)}`}
                  data={weeklyBrandHealthTimeSeriesByDate}
                  lineDataKeys={keysWithoutDate.map(key => ({
                    name: isQv
                      ? queryVolumeShortLabel
                      : String(key).charAt(0).toUpperCase() + String(key).slice(1),
                    dataKey: key,
                    toolTipText: isQv
                      ? `${queryVolumeLongLabel} (${queryVolumeShortLabel}) measures changes in the volume of branded search intent over time. We use ${queryVolumeShortLabel} to proxy for brand equity as it represents a funnel step between awareness and an actual site visit or install.`
                      : "ToolTipText",
                  }))}
                  colorMappings={keysWithoutDate.map((key, index) => ({
                    name: isQv
                      ? queryVolumeShortLabel
                      : String(key).charAt(0).toUpperCase() + String(key).slice(1),
                    color: colors[index],
                  }))}
                  tooltipLabelFormatter={val =>
                    isQv ? queryVolumeShortLabel : capitalizeWords(val)
                  }
                  tooltipFormatter={val =>
                    isQv ? val.toFixed(2) : `${numberFormatter.format(val, 0)}%`
                  }
                  tooltipShape="line"
                  usePercentageYAxis={!isQv}
                  useLeftYAxis={isQv}
                  xAxisDataKey="date"
                  xAxisTickFormatter={val => dateToMddyy(val)}
                />
              </ChartContainer>
            </div>
            <div className="brandEquityRight">
              <DynamicModelOverview
                sections={[
                  {
                    title: "Training Data",
                    rows: [
                      {
                        label: "First model observation",
                        value: dateToMddyy(modelOverview.train_start_date),
                      },
                      {
                        label: "Last model observation",
                        value: dateToMddyy(modelOverview.test_end_date),
                      },
                      {
                        label: "Average weekly spend",
                        value: currencyFormatter.format(modelOverview.mean_spend, 0),
                      },
                      {
                        label: "Outcome variable",
                        value: formatModelOverviewOutcomeVariable(modelOverview.kpi),
                      },
                    ],
                  },
                  {
                    title: "Recency",
                    rows: [
                      {
                        label: "Last refresh date",
                        value: dateToMddyy(modelOverview.refresh_date),
                      },
                    ],
                  },
                  {
                    title: "Statistics",
                    rows: [
                      {
                        label: "Out-of-sample MAPE",
                        value: percFormat(modelOverview.test_mape),
                        tooltipText:
                          "The percentage of variation in the unseen test data outcomes that is accounted for by the model's explanatory variables. Formally, this is 1 - (residual sum of squares) / (total sum of squares)",
                      },
                      {
                        label: "Out-of-sample MAE",
                        value: formatNumber(modelOverview.test_mae, 2),
                        tooltipText:
                          "The percentage of variation in the unseen test data outcomes that is accounted for by the model's explanatory variables. Formally, this is 1 - (residual sum of squares) / (total sum of squares)",
                      },
                    ],
                  },
                  {
                    title: "Key Results",
                    rows: [
                      {
                        label: "Incremental revenue from media-driven brand equity",
                        value: null,
                      },
                      {
                        label: "TTM Incremental Revenue:",
                        value: `$${(
                          incrementalRoasTakeaways.spendInducedBrandRevenue / 1000000
                        ).toFixed(2)}M`,
                      },
                      {
                        label: "TTM Media Spend:",
                        value: `$${(incrementalRoasTakeaways.paidMediaSpend / 1000000).toFixed(
                          2
                        )}M`,
                      },
                      {
                        label: "TTM Incremental ROAS:",
                        value: incrementalRoasTakeaways.iRoasTtm.toFixed(2),
                      },
                    ],
                  },
                ]}
                rightActions={
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownloadModelOverview, pngDownloadModelOverview]}
                    menuOptions={["XLSX", "PNG"]}
                  />
                }
              />
            </div>
          </div>
        )}
    </WidgetContainer>
  );
};

export default BusinessImpactOverview;
